import { memo, ReactElement, useEffect, Ref } from 'react';

import MenuItem from '@mui/material/MenuItem';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import { useData } from '../../contextProviders/DataProvider';
import useLeagues from '../../dataHooks/useLeagues';
import League, { LEAGUE_STATUSES } from '../../types/League';
import GratiSystemError from '../common/GratiSystemError';
import Hourglass from '../common/Hourglass';

interface LeagueSelectorProps {
  value?: string | null;
  defaultValue?: string | null;
  orgId?: string | null;
  onChange: (league: League | undefined) => void;
  showCatchAll?: boolean;
  showOrgNames?: boolean;
  showOnlyAdmined?: boolean;
  disabled?: boolean;
  disableIfSingleOption?: boolean;
  helperText?: string;
  required?: boolean;
  sx?: SxProps<Theme>;
  /** Ref for the underlying input element (for focus management) */
  inputRef?: Ref<HTMLInputElement>;
}

export default memo(function LeagueSelector(props: LeagueSelectorProps): ReactElement {
  const {
    value,
    defaultValue,
    orgId,
    onChange,
    showCatchAll = true,
    showOrgNames = false,
    showOnlyAdmined = false,
    disabled = false,
    disableIfSingleOption = false,
    helperText,
    required = false,
    sx,
    inputRef,
  } = props;
  const { userState, activeMemberships } = useData();

  // Use provided memberships for current user
  const isCurrentUser = userState.status === 'ready' && userState.userProfile;

  const { leagues, isLeaguesLoading, leaguesError } = useLeagues({
    includeOrgAdmined: true,
    includeLeagueAdmined: true,
    includeLeagueMember: !showOnlyAdmined,
    includeCatchAll: showCatchAll,
    person: isCurrentUser ? userState.userProfile : undefined,
    orgId: orgId || undefined,
    status: [LEAGUE_STATUSES.ACTIVE],
    providedMemberships: isCurrentUser && activeMemberships ? {
      memberships: activeMemberships,
      isLoading: false,
      error: undefined,
    } : undefined,
  });

  // When value changes and leagues are loaded, ensure the onChange handler is called with the correct league
  useEffect(() => {
    if (!isLeaguesLoading && leagues?.length) {
      if (value) {
        // If a specific value is provided, find and return that league
        const league = leagues.find((l) => l.itemId === value);
        if (league) {
          onChange(league);
        }
      } else if (value === '') {
        // If empty string is passed, default to first league
        onChange(leagues[0]);
      }
    }
    return () => {
      // enables the react-firebase-hook to do proper unloading
    };
  }, [value, leagues, isLeaguesLoading, leaguesError, onChange]);

  // Don't render the select until leagues are loaded
  if (isLeaguesLoading) {
    return <Hourglass />;
  }

  if (leaguesError) {
    return <GratiSystemError error={leaguesError} />;
  }

  // Don't try to use value/defaultValue until we have leagues to compare against
  const currentValue = leagues?.length
    ? value !== undefined
      ? value || (leagues[0]?.itemId ?? '') // Controlled
      : defaultValue || (leagues[0]?.itemId ?? '') // Uncontrolled
    : '';

  const getDisplayName = (league: League) => {
    return showOrgNames ? `${league.orgName}: ${league.name}` : league.name;
  };

  return (
    <TextField
      select
      required={required}
      fullWidth
      label="League"
      value={currentValue}
      error={required && !currentValue}
      disabled={disabled || isLeaguesLoading || (disableIfSingleOption && leagues?.length === 1)}
      helperText={helperText || (required && !currentValue ? 'League is required' : '')}
      inputRef={inputRef}
      onChange={(e) => {
        const newValue = e.target.value || null;
        const league = leagues?.find((l) => l.itemId === newValue);
        onChange(league);
      }}
      sx={sx}
    >
      {leagues?.map((league) =>
        league ? (
          <MenuItem key={league.itemId} value={league.itemId}>
            {getDisplayName(league)}
          </MenuItem>
        ) : null
      )}
    </TextField>
  );
});
